import React from 'react';
import { Trophy, Star, Crown } from 'lucide-react';
import { cn } from '../utils/cn';

/**
 * PrestigeBadge - Displays user's prestige level with appropriate icon and color
 * Can be used in navigation, profile, leaderboards, etc.
 */
const PrestigeBadge = ({ prestigeLevel, size = 'md', showLabel = true, className }) => {
  if (!prestigeLevel || prestigeLevel === 0) return null;

  const prestigeConfig = {
    1: {
      name: 'Bronze',
      color: '#CD7F32',
      bgColor: 'bg-orange-100 dark:bg-orange-900/20',
      textColor: 'text-orange-800 dark:text-orange-200',
      icon: Trophy,
    },
    2: {
      name: 'Silver',
      color: '#C0C0C0',
      bgColor: 'bg-gray-200 dark:bg-gray-700',
      textColor: 'text-gray-800 dark:text-gray-200',
      icon: Star,
    },
    3: {
      name: 'Gold',
      color: '#FFD700',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
      textColor: 'text-yellow-800 dark:text-yellow-200',
      icon: Crown,
    },
  };

  const config = prestigeConfig[prestigeLevel] || prestigeConfig[1];
  const Icon = config.icon;

  const sizeClasses = {
    xs: {
      container: 'px-1.5 py-0.5 text-xs gap-1',
      icon: 12,
    },
    sm: {
      container: 'px-2 py-1 text-xs gap-1',
      icon: 14,
    },
    md: {
      container: 'px-2.5 py-1.5 text-sm gap-1.5',
      icon: 16,
    },
    lg: {
      container: 'px-3 py-2 text-base gap-2',
      icon: 20,
    },
  };

  const { container, icon } = sizeClasses[size] || sizeClasses.md;

  return (
    <div
      className={cn(
        'inline-flex items-center rounded-full font-semibold',
        config.bgColor,
        config.textColor,
        container,
        className
      )}
    >
      <Icon size={icon} style={{ color: config.color }} />
      {showLabel && (
        <span>
          P{prestigeLevel} {config.name}
        </span>
      )}
    </div>
  );
};

/**
 * PrestigeBadgeList - Displays all earned prestige badges
 */
export const PrestigeBadgeList = ({ badges, className }) => {
  if (!badges || badges.length === 0) {
    return (
      <div className={cn('text-sm text-gray-500 dark:text-gray-400 italic', className)}>
        No prestige badges earned yet
      </div>
    );
  }

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {badges.map((badge, index) => (
        <PrestigeBadge
          key={index}
          prestigeLevel={badge.prestige_level}
          size="sm"
          showLabel={true}
        />
      ))}
    </div>
  );
};

export default PrestigeBadge;
